import React from 'react';
import { Field } from 'formik';
import { tv } from 'tailwind-variants';
import { FormFieldWrapper } from './FormFieldWrapper';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';

const formFieldStyles = tv({
  base: [
    'w-full',
    'min-h-12',
    'px-spacing-spacing-16',
    'py-spacing-spacing-12',
    'border',
    'border-color-input-border',
    'rounded-[8px]',
    'bg-color-grayscale-white',
    'placeholder:text-color-text-text-secondary',
    'text-color-text-text',
    'hover:border-color-input-border-hover',
    'font-typography-font-family-body',
    'text-typography-font-size-body-medium',
    'leading-typography-line-height-body-medium',
    'placeholder:italic',
    'font-normal',
    'disabled:text-color-text-text-disabled',
    'disabled:cursor-not-allowed',
    'focus:outline-none',
    'focus:ring-1',
    'focus:border-color-input-border-focused',
    'focus:ring-color-input-border-focused',
    'focus:ring-shadow-[0_0_0_1px_var(--color-core-color-4-SkyBlue)]',
  ],
  variants: {
    state: {
      default: '',
      error: [
        'border-color-text-text-critical',
        'focus:border-color-text-text-error',
        'bg-color-fill-error-light',
      ],
      disabled: ['bg-color-grayscale-gray-100', 'cursor-not-allowed'],
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

interface EmailFieldProps {
  field: CustomForm.FormField_Item;
  hasError?: boolean;
  isTouched?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  errors?: Record<string, string | undefined>;
  touched?: Record<string, boolean | undefined>;
  hideLabel?: boolean;
  isStandalone?: boolean;
}

export const EmailField: React.FC<EmailFieldProps> = ({
  field,
  hasError = false,
  value,
  onChange,
  onBlur,
  errors,
  touched,
  hideLabel,
  isStandalone = false,
}) => {
  const fieldConfig = field.fields;
  const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
  const label = fieldConfig?.label?.value || fieldName || '';
  const helpText = fieldConfig?.helpText?.value;
  const disabled = fieldConfig?.disabled?.value;
  const required = fieldConfig?.required?.value;
  const characterLimit = fieldConfig?.characterLimit?.value;
  const placeholder = fieldConfig?.placeholderText?.value || 'Enter your email address';

  if (!fieldName) return null;

  const fieldState = disabled ? 'disabled' : hasError ? 'error' : 'default';

  const commonFieldProps = {
    name: fieldName,
    id: fieldName,
    disabled,
    className: formFieldStyles({ state: fieldState }),
    'data-automation': `emailField-${fieldName}`,
  };

  return (
    <FormFieldWrapper
      key={fieldName}
      fieldName={fieldName}
      label={label}
      helpText={helpText}
      isDisabled={disabled}
      hasError={hasError}
      isRequired={required}
      errors={errors}
      touched={touched}
      hideLabel={hideLabel}
    >
      {isStandalone ? (
        <input
          type="email"
          maxLength={characterLimit}
          placeholder={placeholder}
          aria-required={required}
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={onBlur}
          {...commonFieldProps}
        />
      ) : (
        <Field
          type="email"
          maxLength={characterLimit}
          placeholder={placeholder}
          aria-required={required}
          {...commonFieldProps}
        />
      )}
    </FormFieldWrapper>
  );
};

export default EmailField;
